import React, { useMemo, useState } from 'react';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Chip,
  TablePagination,
  TableSortLabel,
  Typography,
  CircularProgress,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { Visibility } from '@mui/icons-material';
import { AttendanceRecord } from '../../../types';
import { getConfiguredApiUrl } from '../../../config/runtimeConfig';

type SortDirection = 'asc' | 'desc';
type SortColumn =
  | 'employeeName'
  | 'employeeCode'
  | 'department'
  | 'date'
  | 'attendanceStatus'
  | 'isLate'
  | 'checkInTime'
  | 'checkOutTime'
  | 'time'
  | 'type'
  | 'modeOfEntry'
  | 'locationName'
  | 'selfieImagePath'
  | 'recordedBy';

const columns: Array<{ key: SortColumn; label: string }> = [
  { key: 'employeeName', label: 'Employee Name' },
  { key: 'employeeCode', label: 'Employee Code' },
  { key: 'department', label: 'Department' },
  { key: 'date', label: 'Date' },
  { key: 'attendanceStatus', label: 'Status' },
  { key: 'isLate', label: 'Late' },
  { key: 'checkInTime', label: 'Check-In' },
  { key: 'checkOutTime', label: 'Check-Out' },
  { key: 'time', label: 'Time' },
  { key: 'type', label: 'Type' },
  { key: 'modeOfEntry', label: 'Mode' },
  { key: 'locationName', label: 'Location' },
  { key: 'selfieImagePath', label: 'Selfie' },
  { key: 'recordedBy', label: 'Recorded By' },
];

interface AttendanceTableProps {
  records: AttendanceRecord[];
  loading: boolean;
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onDateSortChange?: (direction: SortDirection) => void;
}

// ✅ Check In → Green, Check Out → Light Orange
const getTypeStyle = (type: string): { backgroundColor: string; color: string } => {
  const t = (type ?? '').toLowerCase().replace(/\s+/g, '');
  if (t === 'checkin')  return { backgroundColor: '#4CAF50', color: '#fff' }; // Green
  if (t === 'checkout') return { backgroundColor: '#FFB74D', color: '#fff' }; // Light Orange
  return { backgroundColor: '#E0E0E0', color: '#555' };
};

// ✅ Status colors matching the legend image exactly
const getStatusStyle = (status: string): { backgroundColor: string; color: string } => {
  switch ((status ?? '').toLowerCase().replace(/\s+/g, '')) {
    case 'present':  return { backgroundColor: '#4CAF50', color: '#fff' }; // 🟢 Green
    case 'absent':   return { backgroundColor: '#F44336', color: '#fff' }; // 🔴 Red
    case 'holiday':  return { backgroundColor: '#FF7043', color: '#fff' }; // 🟠 Orange-Red
    case 'leave':    return { backgroundColor: '#FFA726', color: '#fff' }; // 🟡 Yellow-Orange
    case 'weekoff':  return { backgroundColor: '#42A5F5', color: '#fff' }; // 🔵 Blue
    case 'compoff':  return { backgroundColor: '#AB47BC', color: '#fff' }; // 🟣 Purple
    case 'halfday':  return { backgroundColor: '#FFCA28', color: '#333' }; // Amber
    default:         return { backgroundColor: '#E0E0E0', color: '#555' };
  }
};

const formatDate = (date: string): string => {
  if (!date) return '-';
  if (/^\d{2}-\d{2}-\d{4}$/.test(date)) {
    const [day, month, year] = date.split('-');
    const parsed = new Date(`${year}-${month}-${day}T00:00:00`);
    if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleDateString('en-GB');
    return date;
  }
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString('en-GB');
};

const getDateSortValue = (date: string): number => {
  if (!date) return 0;

  const trimmed = date.trim();
  const dayFirstMatch = trimmed.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (dayFirstMatch) {
    return new Date(
      Number(dayFirstMatch[3]),
      Number(dayFirstMatch[2]) - 1,
      Number(dayFirstMatch[1])
    ).getTime();
  }

  const isoDateMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (isoDateMatch) {
    return new Date(
      Number(isoDateMatch[1]),
      Number(isoDateMatch[2]) - 1,
      Number(isoDateMatch[3])
    ).getTime();
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const getSortValue = (row: AttendanceRecord, column: SortColumn): string | number => {
  if (column === 'date') {
    return getDateSortValue(row.date);
  }

  if (column === 'isLate') return row.isLate ? 1 : 0;
  if (column === 'selfieImagePath') return row.selfieImagePath ? 1 : 0;

  const value = row[column];
  const text = value == null || value === '' ? '-' : String(value);
  const numeric = Number(text.replace(/,/g, ''));
  if (text !== '-' && !Number.isNaN(numeric)) return numeric;

  return text.toLowerCase();
};

const getLocationButtonLabel = (type: string): string => {
  const normalizedType = (type ?? '').toLowerCase().replace(/\s+/g, '');
  if (normalizedType === 'checkin') return 'Check-In Map';
  if (normalizedType === 'checkout') return 'Check-Out Map';
  return 'View Map';
};

const AttendanceTable: React.FC<AttendanceTableProps> = ({
  records,
  loading,
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onDateSortChange,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{ column: SortColumn; direction: SortDirection } | null>(null);
  const rowsPerPageOptions = [
    10,
    50,
    100,
    { label: 'All', value: -1 },
  ];

  const sortedRecords = useMemo(() => {
    if (!sortConfig) return records;

    return [...records].sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.column);
      const bValue = getSortValue(b, sortConfig.column);

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const compare = String(aValue).localeCompare(String(bValue), undefined, {
        numeric: true,
        sensitivity: 'base',
      });

      return sortConfig.direction === 'asc' ? compare : -compare;
    });
  }, [records, sortConfig]);

  const handleSort = (column: SortColumn) => {
    const nextDirection =
      sortConfig?.column === column && sortConfig.direction === 'asc' ? 'desc' : 'asc';

    setSortConfig({
      column,
      direction: nextDirection,
    });

    if (column === 'date') {
      onDateSortChange?.(nextDirection);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <TableContainer component={Paper}>
        <Table
          size="small"
          sx={{
            '& td, & th': {
              py: 0.3,
              px: 0.8,
              fontSize: '12px',
              whiteSpace: 'nowrap',
            },
            '& .MuiTableRow-root': {
              height: 36,
            },
          }}
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              {columns.map((column) => (
                <TableCell key={column.key}>
                  <TableSortLabel
                    active={sortConfig?.column === column.key}
                    direction={sortConfig?.column === column.key ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort(column.key)}
                  >
                    <strong>{column.label}</strong>
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRecords.length === 0 ? (
              <TableRow>
                <TableCell colSpan={14} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No attendance records found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedRecords.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.employeeName}</TableCell>
                  <TableCell>{row.employeeCode || '-'}</TableCell>
                  <TableCell>{row.department || '-'}</TableCell>
                  <TableCell>{formatDate(row.date)}</TableCell>

                  {/* ✅ Status chip with custom color */}
                  <TableCell>
                    {row.attendanceStatus ? (
                      <Chip
                        label={row.attendanceStatus}
                        size="small"
                        sx={{
                          ...getStatusStyle(row.attendanceStatus),
                          fontWeight: 500,
                          fontSize: '11px',
                        }}
                      />
                    ) : '-'}
                  </TableCell>

                  <TableCell>
                    {row.isLate ? (
                      <Chip label="Late" color="warning" size="small" variant="outlined" />
                    ) : '-'}
                  </TableCell>
                  <TableCell>{row.checkInTime || '-'}</TableCell>
                  <TableCell>{row.checkOutTime || '-'}</TableCell>
                  <TableCell>{row.time || '-'}</TableCell>

                  {/* ✅ Type chip with custom color */}
                  <TableCell>
                    <Chip
                      label={row.type}
                      size="small"
                      sx={{
                        ...getTypeStyle(row.type),
                        fontWeight: 500,
                        fontSize: '11px',
                      }}
                    />
                  </TableCell>

                  <TableCell>{row.modeOfEntry || '-'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                      <Typography variant="body2">
                        {row.locationName || '-'}
                      </Typography>
                      {row.viewLocation && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          href={row.viewLocation}
                          target="_blank"
                          rel="noopener noreferrer"
                          endIcon={<OpenInNew fontSize="small" />}
                        >
                          {getLocationButtonLabel(row.type)}
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {row.selfieImagePath ? (
                      (() => {
                        const path = String(row.selfieImagePath || '');
                        const apiBase = getConfiguredApiUrl().replace(/\/?api\/?$/i, '');
                        const url = /^https?:\/\//i.test(path) ? path : `${apiBase}/${path.replace(/^\/+/, '')}`;
                        return (
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <img
                              src={url}
                              alt="Selfie"
                              loading="lazy"
                              style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                            />
                            <IconButton size="small" onClick={() => setPreviewUrl(url)} aria-label="view selfie">
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Box>
                        );
                      })()
                    ) : (
                      <Typography variant="body2" color="text.secondary">No Image</Typography>
                    )}
                  </TableCell>
                  <TableCell>{row.recordedBy || '-'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(previewUrl)} onClose={() => setPreviewUrl(null)} maxWidth="md" fullWidth>
        <DialogContent>
          {previewUrl ? (
            <img src={previewUrl} alt="Preview" style={{ width: '100%', height: 'auto', borderRadius: 6 }} />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewUrl(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, newPage) => onPageChange(newPage)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
        rowsPerPageOptions={rowsPerPageOptions}
      />
    </>
  );
};

export default AttendanceTable;
